import React, { useEffect, useState } from 'react';
import { api } from '../api/client';

interface MRequest {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  priority: string;
  response: string | null;
  resident_id: string | null;
  unit_id: string | null;
  assigned_to: string | null;
  created_at: string | null;
  scheduled_date: string | null;
  due_date: string | null;
  estimated_cost: number | null;
  actual_cost: number | null;
  work_notes: string | null;
  vendor: string | null;
}

interface AdminUserOption {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

interface Stats {
  total: number;
  open: number;
  in_progress: number;
  resolved: number;
  urgent: number;
  overdue: number;
}

const CATEGORIES: Record<string, string> = {
  plumbing: 'Сантехник',
  electrical: 'Цахилгаан',
  elevator: 'Лифт',
  heating: 'Халаалт',
  cleaning: 'Цэвэрлэгээ',
  security: 'Аюулгүй байдал',
  parking: 'Зогсоол',
  noise: 'Чимээ шуугиан',
  suggestion: 'Санал хүсэлт',
  complaint: 'Гомдол',
  other: 'Бусад',
};

const STATUSES: Record<string, { label: string; bg: string; color: string }> = {
  open: { label: 'Нээлттэй', bg: '#dbeafe', color: '#1d4ed8' },
  in_progress: { label: 'Шийдвэрлэж байна', bg: '#fef3c7', color: '#92400e' },
  resolved: { label: 'Шийдсэн', bg: '#d1fae5', color: '#065f46' },
  closed: { label: 'Хаасан', bg: '#e5e7eb', color: '#374151' },
  rejected: { label: 'Татгалзсан', bg: '#fee2e2', color: '#991b1b' },
};

const PRIORITIES: Record<string, { label: string; bg: string; color: string }> = {
  low: { label: 'Бага', bg: '#e5e7eb', color: '#374151' },
  medium: { label: 'Дунд', bg: '#dbeafe', color: '#1d4ed8' },
  high: { label: 'Өндөр', bg: '#fed7aa', color: '#9a3412' },
  urgent: { label: 'Яаралтай', bg: '#fee2e2', color: '#991b1b' },
};

export default function MaintenancePage() {
  const [requests, setRequests] = useState<MRequest[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState<MRequest | null>(null);
  const [form, setForm] = useState({ title: '', description: '', category: 'other', priority: 'medium' });
  const [responseText, setResponseText] = useState('');
  const [sending, setSending] = useState(false);
  const [adminUsers, setAdminUsers] = useState<AdminUserOption[]>([]);
  const [assignUserId, setAssignUserId] = useState('');
  const [scheduleForm, setScheduleForm] = useState({ scheduled_date: '', due_date: '', estimated_cost: '', vendor: '' });
  const [completeForm, setCompleteForm] = useState({ actual_cost: '', work_notes: '' });

  const fetchData = async () => {
    try {
      const params = filter ? `?status=${filter}` : '';
      const [reqData, statsData, usersData] = await Promise.all([
        api.get(`/admin/requests${params}`),
        api.get('/admin/requests/stats'),
        api.get('/admin/users').catch(() => ({ items: [] })),
      ]);
      setRequests(reqData.items || []);
      setStats(statsData);
      setAdminUsers((usersData.items || usersData || []).map((u: any) => ({
        id: u.id, first_name: u.first_name, last_name: u.last_name, email: u.email, role: u.role,
      })));
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filter]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title) return;
    setSending(true);
    try {
      await api.post('/admin/requests', form);
      setForm({ title: '', description: '', category: 'other', priority: 'medium' });
      setShowForm(false);
      fetchData();
    } catch {} finally { setSending(false); }
  };

  const handleStatusChange = async (id: string, status: string, response?: string) => {
    try {
      const body: any = { status };
      if (response) body.response = response;
      await api.put(`/admin/requests/${id}`, body);
      fetchData();
      if (showDetail?.id === id) {
        setShowDetail(prev => prev ? { ...prev, status, response: response || prev.response } : null);
      }
    } catch {}
  };

  const handleResponse = async (id: string) => {
    if (!responseText.trim()) return;
    try {
      await api.put(`/admin/requests/${id}`, { response: responseText });
      setResponseText('');
      fetchData();
      if (showDetail?.id === id) {
        setShowDetail(prev => prev ? { ...prev, response: responseText } : null);
      }
    } catch {}
  };

  const handleAssign = async (requestId: string, userId: string) => {
    try {
      await api.post(`/admin/requests/${requestId}/assign`, { admin_user_id: userId });
      setAssignUserId('');
      fetchData();
      if (showDetail?.id === requestId) {
        setShowDetail(prev => prev ? { ...prev, assigned_to: userId, status: 'in_progress' } : null);
      }
    } catch {}
  };

  const handleResolve = async (requestId: string) => {
    try {
      await api.post(`/admin/requests/${requestId}/resolve`, { response: responseText || '' });
      setResponseText('');
      fetchData();
      if (showDetail?.id === requestId) {
        setShowDetail(prev => prev ? { ...prev, status: 'resolved' } : null);
      }
    } catch {}
  };

  const handleSchedule = async (requestId: string) => {
    try {
      await api.post(`/admin/requests/${requestId}/schedule`, {
        scheduled_date: scheduleForm.scheduled_date || null,
        due_date: scheduleForm.due_date || null,
        estimated_cost: scheduleForm.estimated_cost ? parseFloat(scheduleForm.estimated_cost) : null,
        vendor: scheduleForm.vendor || null,
      });
      setScheduleForm({ scheduled_date: '', due_date: '', estimated_cost: '', vendor: '' });
      fetchData();
      const updated = await api.get(`/admin/requests?status=`);
      const item = (updated.items || []).find((r: any) => r.id === requestId);
      if (item) setShowDetail(item);
    } catch {}
  };

  const handleComplete = async (requestId: string) => {
    try {
      await api.post(`/admin/requests/${requestId}/complete`, {
        actual_cost: completeForm.actual_cost ? parseFloat(completeForm.actual_cost) : null,
        work_notes: completeForm.work_notes || null,
        response: responseText || '',
      });
      setCompleteForm({ actual_cost: '', work_notes: '' });
      setResponseText('');
      fetchData();
      if (showDetail?.id === requestId) {
        setShowDetail(prev => prev ? { ...prev, status: 'resolved' } : null);
      }
    } catch {}
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "'Inter'", fontSize: 24, fontWeight: 700, color: '#1c2b22', margin: 0 }}>
            Өргөдөл / Гомдол
          </h1>
          <p style={{ color: '#5f6f66', fontSize: 14, marginTop: 4 }}>Засвар үйлчилгээний хүсэлтүүд</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} style={btnPrimary}>
          {showForm ? 'Хаах' : '+ Шинэ өргөдөл'}
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Нийт', value: stats.total, bg: '#f3f4f6', color: '#1f2937' },
            { label: 'Нээлттэй', value: stats.open, bg: '#dbeafe', color: '#1d4ed8' },
            { label: 'Шийдвэрлэж буй', value: stats.in_progress, bg: '#fef3c7', color: '#92400e' },
            { label: 'Шийдсэн', value: stats.resolved, bg: '#d1fae5', color: '#065f46' },
            { label: 'Яаралтай', value: stats.urgent, bg: '#fee2e2', color: '#991b1b' },
            { label: 'Хугацаа хэтэрсэн', value: stats.overdue, bg: '#fce7f3', color: '#9d174d' },
          ].map((s, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: s.color, fontFamily: "'Inter'" }}>{s.value}</div>
              <div style={{ fontSize: 13, color: '#5f6f66', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { key: '', label: 'Бүгд' },
          { key: 'open', label: 'Нээлттэй' },
          { key: 'in_progress', label: 'Шийдвэрлэж буй' },
          { key: 'resolved', label: 'Шийдсэн' },
          { key: 'closed', label: 'Хаасан' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: filter === f.key ? 600 : 400,
            background: filter === f.key ? '#236844' : '#fff',
            color: filter === f.key ? '#fff' : '#5f6f66',
          }}>{f.label}</button>
        ))}
      </div>

      {/* Create Form */}
      {showForm && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <h3 style={{ fontFamily: "'Inter'", fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>Шинэ өргөдөл бүртгэх</h3>
          <form onSubmit={handleCreate}>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Гарчиг *</label>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} style={inputStyle} placeholder="Лифт эвдэрсэн" required />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Тайлбар</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} placeholder="Дэлгэрэнгүй тайлбар..." />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={labelStyle}>Ангилал</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={inputStyle}>
                  {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Ач холбогдол</label>
                <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} style={inputStyle}>
                  {Object.entries(PRIORITIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <button type="submit" disabled={sending} style={{ ...btnPrimary, opacity: sending ? 0.6 : 1 }}>
              {sending ? 'Бүртгэж байна...' : 'Бүртгэх'}
            </button>
          </form>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}
          onClick={() => setShowDetail(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, maxWidth: 560, width: '90%', maxHeight: '80vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 20 }}>
              <h2 style={{ fontFamily: "'Inter'", fontSize: 20, fontWeight: 700, color: '#1c2b22', margin: 0 }}>{showDetail.title}</h2>
              <button onClick={() => setShowDetail(null)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#9ca3af' }}>×</button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <Badge {...(STATUSES[showDetail.status] || { label: showDetail.status, bg: '#e5e7eb', color: '#374151' })} />
              <Badge {...(PRIORITIES[showDetail.priority] || { label: showDetail.priority, bg: '#e5e7eb', color: '#374151' })} />
              <Badge label={CATEGORIES[showDetail.category] || showDetail.category} bg="#f3e8ff" color="#7c3aed" />
            </div>
            {showDetail.description && (
              <div style={{ background: '#faf9f5', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#5f6f66', marginBottom: 4 }}>Тайлбар</div>
                <div style={{ fontSize: 14, color: '#1c2b22', lineHeight: 1.6 }}>{showDetail.description}</div>
              </div>
            )}
            {showDetail.response && (
              <div style={{ background: '#f0fdf4', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: '#065f46', marginBottom: 4 }}>Хариу</div>
                <div style={{ fontSize: 14, color: '#1c2b22', lineHeight: 1.6 }}>{showDetail.response}</div>
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Хариу бичих</label>
              <textarea value={responseText} onChange={e => setResponseText(e.target.value)} style={{ ...inputStyle, minHeight: 60 }} placeholder="Хариу..." />
              <button onClick={() => handleResponse(showDetail.id)} style={{ ...btnPrimary, marginTop: 8 }}>Хариу илгээх</button>
            </div>
            {/* Assign */}
            <div style={{ marginBottom: 16, background: '#f8fafc', borderRadius: 8, padding: 12 }}>
              <label style={{ ...labelStyle, fontSize: 12, color: '#64748b' }}>Хариуцагч томилох</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select value={assignUserId} onChange={e => setAssignUserId(e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}>
                  <option value="">— Сонгох —</option>
                  {adminUsers.map(u => (
                    <option key={u.id} value={u.id}>{u.last_name} {u.first_name} ({u.email})</option>
                  ))}
                </select>
                <button disabled={!assignUserId} onClick={() => handleAssign(showDetail.id, assignUserId)}
                  style={{ ...btnPrimary, opacity: assignUserId ? 1 : 0.5, padding: '8px 16px', fontSize: 13 }}>
                  Томилох
                </button>
              </div>
              {showDetail.assigned_to && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#059669' }}>
                  Томилогдсон: {adminUsers.find(u => u.id === showDetail.assigned_to)?.email || showDetail.assigned_to}
                </div>
              )}
            </div>

            {/* Work Order Info */}
            {(showDetail.scheduled_date || showDetail.estimated_cost || showDetail.vendor) && (
              <div style={{ background: '#f0f9ff', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0369a1', marginBottom: 8 }}>Ажлын захиалга</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13, color: '#1c2b22' }}>
                  {showDetail.scheduled_date && <div>Хуваарь: <b>{showDetail.scheduled_date}</b></div>}
                  {showDetail.due_date && <div>Хугацаа: <b>{showDetail.due_date}</b></div>}
                  {showDetail.estimated_cost != null && <div>Төсөв: <b>₮{showDetail.estimated_cost.toLocaleString()}</b></div>}
                  {showDetail.actual_cost != null && <div>Бодит: <b>₮{showDetail.actual_cost.toLocaleString()}</b></div>}
                  {showDetail.vendor && <div>Гүйцэтгэгч: <b>{showDetail.vendor}</b></div>}
                </div>
                {showDetail.work_notes && <div style={{ marginTop: 8, fontSize: 13, color: '#374151' }}>Тэмдэглэл: {showDetail.work_notes}</div>}
              </div>
            )}

            {/* Schedule Work */}
            {['open', 'in_progress'].includes(showDetail.status) && (
              <div style={{ background: '#fffbeb', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 8 }}>Ажлын хуваарь</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <input type="date" placeholder="Хуваарьт огноо" value={scheduleForm.scheduled_date} onChange={e => setScheduleForm(f => ({ ...f, scheduled_date: e.target.value }))} style={{ ...inputStyle, fontSize: 12 }} />
                  <input type="date" placeholder="Дуусах хугацаа" value={scheduleForm.due_date} onChange={e => setScheduleForm(f => ({ ...f, due_date: e.target.value }))} style={{ ...inputStyle, fontSize: 12 }} />
                  <input type="number" placeholder="Төсөвт зардал (₮)" value={scheduleForm.estimated_cost} onChange={e => setScheduleForm(f => ({ ...f, estimated_cost: e.target.value }))} style={{ ...inputStyle, fontSize: 12 }} />
                  <input placeholder="Гүйцэтгэгч" value={scheduleForm.vendor} onChange={e => setScheduleForm(f => ({ ...f, vendor: e.target.value }))} style={{ ...inputStyle, fontSize: 12 }} />
                </div>
                <button onClick={() => handleSchedule(showDetail.id)} style={{ ...btnPrimary, marginTop: 8, padding: '6px 14px', fontSize: 12, background: '#d97706' }}>Хуваарьлах</button>
              </div>
            )}

            {/* Complete Work */}
            {showDetail.status === 'in_progress' && (
              <div style={{ background: '#ecfdf5', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#065f46', marginBottom: 8 }}>Ажил дуусгах</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <input type="number" placeholder="Бодит зардал (₮)" value={completeForm.actual_cost} onChange={e => setCompleteForm(f => ({ ...f, actual_cost: e.target.value }))} style={{ ...inputStyle, fontSize: 12 }} />
                  <input placeholder="Тэмдэглэл" value={completeForm.work_notes} onChange={e => setCompleteForm(f => ({ ...f, work_notes: e.target.value }))} style={{ ...inputStyle, fontSize: 12 }} />
                </div>
                <button onClick={() => handleComplete(showDetail.id)} style={{ ...btnPrimary, marginTop: 8, padding: '6px 14px', fontSize: 12, background: '#059669' }}>Дуусгах</button>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['open', 'rejected', 'closed'].includes(showDetail.status) && (
                <button onClick={() => handleStatusChange(showDetail.id, 'in_progress')} style={btnOutline}>Шийдвэрлэж эхлэх</button>
              )}
              {['in_progress', 'open'].includes(showDetail.status) && (
                <button onClick={() => handleResolve(showDetail.id)} style={{ ...btnPrimary, background: '#059669' }}>Шийдсэн</button>
              )}
              {['open', 'in_progress'].includes(showDetail.status) && (
                <button onClick={() => handleStatusChange(showDetail.id, 'rejected')} style={{ ...btnOutline, color: '#dc2626', borderColor: '#dc2626' }}>Татгалзах</button>
              )}
              {showDetail.status === 'resolved' && (
                <button onClick={() => handleStatusChange(showDetail.id, 'closed')} style={btnOutline}>Хаах</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#5f6f66' }}>Ачаалж байна...</div>
        ) : requests.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#5f6f66' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <p style={{ margin: 0 }}>Өргөдөл байхгүй</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#faf9f5', borderBottom: '1px solid #e8e5dc' }}>
                <th style={thStyle}>Гарчиг</th>
                <th style={thStyle}>Ангилал</th>
                <th style={thStyle}>Ач холбогдол</th>
                <th style={thStyle}>Төлөв</th>
                <th style={thStyle}>Огноо</th>
                <th style={thStyle}>Үйлдэл</th>
              </tr>
            </thead>
            <tbody>
              {requests.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #f0ede4', cursor: 'pointer' }}
                  onClick={() => { setShowDetail(r); setResponseText(''); }}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 500 }}>{r.title}</div>
                    {r.description && <div style={{ fontSize: 12, color: '#5f6f66', marginTop: 2 }}>{r.description.slice(0, 60)}{r.description.length > 60 ? '...' : ''}</div>}
                  </td>
                  <td style={tdStyle}><Badge label={CATEGORIES[r.category] || r.category} bg="#f3e8ff" color="#7c3aed" /></td>
                  <td style={tdStyle}><Badge {...(PRIORITIES[r.priority] || { label: r.priority, bg: '#e5e7eb', color: '#374151' })} /></td>
                  <td style={tdStyle}><Badge {...(STATUSES[r.status] || { label: r.status, bg: '#e5e7eb', color: '#374151' })} /></td>
                  <td style={{ ...tdStyle, fontSize: 13, color: '#5f6f66' }}>{r.created_at ? new Date(r.created_at).toLocaleDateString('mn-MN') : '-'}</td>
                  <td style={tdStyle}>
                    <button onClick={e => { e.stopPropagation(); setShowDetail(r); setResponseText(''); }} style={linkBtn}>Дэлгэрэнгүй</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Badge({ label, bg, color }: { label: string; bg: string; color: string }) {
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: bg, color }}>{label}</span>
  );
}

const btnPrimary: React.CSSProperties = {
  background: 'linear-gradient(180deg, #2f7d52, #236844)', color: '#fff', border: 'none',
  padding: '10px 20px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: "'Inter'",
};
const btnOutline: React.CSSProperties = {
  background: 'transparent', color: '#236844', border: '1.5px solid #236844',
  padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13,
};
const labelStyle: React.CSSProperties = { display: 'block', marginBottom: 6, fontSize: 13, color: '#2a3b32', fontWeight: 500 };
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 14px', border: '1.5px solid #dcdacf', borderRadius: 10,
  fontSize: 14, boxSizing: 'border-box', background: '#fbfaf6',
};
const thStyle: React.CSSProperties = { padding: '12px 16px', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#5f6f66' };
const tdStyle: React.CSSProperties = { padding: '12px 16px', fontSize: 14, color: '#1c2b22' };
const linkBtn: React.CSSProperties = {
  background: 'none', border: '1px solid #dcdacf', padding: '5px 12px',
  borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#236844', fontWeight: 600,
};
